import React, { useState, useEffect } from 'react';
import { DailyCheckIn } from '@/api/entities';
import { X, Check, Wind, Book, Feather, Target, Edit, Save, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const periodiRicompense = [
  { id: "ricompensa_1_giorno",     label: "Dopo 1 giorno",     labelCanon: "1 Giorno" },
  { id: "ricompensa_3_giorni",     label: "Dopo 3 giorni",     labelCanon: "3 Giorni" },
  { id: "ricompensa_1_settimana",  label: "Dopo 1 settimana",  labelCanon: "1 Settimana" },
  { id: "ricompensa_2_settimane",  label: "Dopo 2 settimane",  labelCanon: "2 Settimane" },
  { id: "ricompensa_3_settimane",  label: "Dopo 3 settimane",  labelCanon: "3 Settimane" },
  { id: "ricompensa_1_mese",       label: "Dopo 1 mese",       labelCanon: "1 Mese" },
  { id: "ricompensa_2_mesi",       label: "Dopo 2 mesi",       labelCanon: "2 Mesi" },
  { id: "ricompensa_6_mesi",       label: "Dopo 6 mesi",       labelCanon: "6 Mesi" },
  { id: "ricompensa_1_anno",       label: "Dopo 1 anno",       labelCanon: "1 Anno" },
  { id: "ricompensa_2_anni",       label: "Dopo 2 anni",       labelCanon: "2 Anni" },
  { id: "ricompensa_5_anni",       label: "Dopo 5 anni",       labelCanon: "5 Anni" },
  { id: "ricompensa_10_anni",      label: "Dopo 10 anni",      labelCanon: "10 Anni" },
];

// helper per recuperare il check-in con tutti gli alias che usiamo nel calendario
function getCI(dayData) {
  return dayData?.checkIn || dayData?.dailyCheckIn || dayData?.checkin || null;
}

/* 🔧 NEW: normalizza la mappa ricompense (string JSON o oggetto) in { "1 Giorno": "premio", ... } */
function rewardsByLabelCanon(raw) {
  let o = raw;
  if (typeof o === 'string') {
    try { o = JSON.parse(o); } catch { return {}; }
  }
  if (o && typeof o === 'object' && o.rewards && typeof o.rewards === 'object') {
    o = o.rewards;
  }
  if (!o || typeof o !== 'object') return {};
  const out = {};
  for (const p of periodiRicompense) {
    const v = o[p.id] ?? o[p.labelCanon] ?? o[p.label] ?? o[p.labelCanon?.toLowerCase?.()];
    if (v != null && v !== '') out[p.labelCanon] = String(v);
  }
  return out;
}

export default function DayDetailModal({ dayData, date, onClose }) {
  const ci = getCI(dayData);
  const [note, setNote] = useState(ci?.note || '');
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fresh = getCI(dayData);
    setNote(fresh?.note || '');
    setIsEditingNote(false);
  }, [dayData]);

  const handleSaveNote = async () => {
    setIsSaving(true);
    try {
      const cur = getCI(dayData);
      if (cur?.id) {
        await DailyCheckIn.update(cur.id, { note });
        if (dayData.checkIn) dayData.checkIn.note = note;
        if (dayData.dailyCheckIn) dayData.dailyCheckIn.note = note;
      } else {
        console.warn('Check-in non esistente, impossibile salvare la nota.');
      }
      setIsEditingNote(false);
      window.dispatchEvent(new CustomEvent('rb:checkin-saved'));
    } catch (error) {
      console.error('Errore nel salvare la nota:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const ActivityItem = ({ icon: Icon, label, completed }) => (
    <div className="flex items-center gap-3 text-gray-700">
      <Icon className={`w-5 h-5 ${completed ? 'text-green-500' : 'text-gray-400'}`} />
      <span className={completed ? '' : 'text-gray-500'}>{label}</span>
      {completed && <Check className="w-5 h-5 text-green-500 ml-auto" />}
    </div>
  );

  // Fallback dei campi del check-in (alcuni sono opzionali)
  const feeling = ci?.q1_feeling ?? ci?.feeling ?? null;
  const motivation = ci?.q2_motivation ?? ci?.motivation ?? null;
  const breathing = (ci?.q3_breathing ?? ci?.status ?? '').replace('_', ' ');
  const hasStructured = feeling != null || motivation != null || (ci?.q3_breathing != null);

  /* 🔧 NEW: ricava la reward da mostrare nel riquadro Traguardo.
     Priorità: dayData.goal.reward → (fallback) da ricompense esercizio 6 del check-in */
  const inlineReward = (() => {
    const fromGoal = dayData?.goal?.reward;
    if (fromGoal) return fromGoal;
    const map = rewardsByLabelCanon(getCI(dayData)?.ricompense_obiettivi);
    const lbl = dayData?.goal?.label;
    return lbl ? (map[lbl] || null) : null;
  })();

  return (
    <AnimatePresence>
      {date && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">Dettagli del Giorno</h2>
                  <p className="text-lg font-medium text-blue-600">
                    {format(date, "EEEE, d MMMM yyyy", { locale: it })}
                  </p>
                </div>
                <Button variant="ghost" size="icon" onClick={onClose} className="rounded-full">
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="mt-6 space-y-6">
                {/* Goal section */}
                {dayData?.goal && (
                  <div className="p-4 border-2 rounded-xl bg-amber-50 border-amber-300 shadow-inner">
                    <h3 className="font-bold text-lg mb-2 flex items-center gap-2 text-amber-800">
                      <Award /> Traguardo Raggiunto!
                    </h3>
                    <p className="font-semibold">{dayData.goal.label}</p>
                    {inlineReward && (
                      <div className="mt-2 text-sm">
                        <p><strong>La tua ricompensa:</strong></p>
                        <blockquote className="pl-3 border-l-4 border-amber-400 text-amber-900 italic bg-amber-100 p-2 rounded-md">
                          {inlineReward}
                        </blockquote>
                      </div>
                    )}
                  </div>
                )}

                {/* Check-in section */}
                <div className="p-4 border rounded-xl">
                  <h3 className="font-semibold mb-3">Check-in Giornaliero</h3>

                  {ci ? (
                    hasStructured ? (
                      <div className="space-y-2 text-sm">
                        {feeling != null && (
                          <p><strong>Umore:</strong> <Badge variant="outline">{feeling}/10</Badge></p>
                        )}
                        {motivation != null && (
                          <p><strong>Motivazione:</strong> <Badge variant="outline">{motivation}/10</Badge></p>
                        )}
                        {(ci.q3_breathing ?? ci.status) && (
                          <p><strong>Respirazione:</strong> <Badge variant="secondary">{breathing}</Badge></p>
                        )}
                        {(ci.cigarettes_avoided || ci.savings_eur) && (
                          <>
                            <p><strong>Sigarette evitate:</strong> {ci.cigarettes_avoided || 0}</p>
                            <p><strong>Risparmio:</strong> €{Number(ci.savings_eur || 0).toFixed(2)}</p>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2 text-sm">
                        <p><strong>Stato:</strong> <Badge variant="secondary">{breathing || 'success'}</Badge></p>
                        {(ci.cigarettes_avoided || ci.savings_eur) && (
                          <>
                            <p><strong>Sigarette evitate:</strong> {ci.cigarettes_avoided || 0}</p>
                            <p><strong>Risparmio:</strong> €{Number(ci.savings_eur || 0).toFixed(2)}</p>
                          </>
                        )}
                        {!ci.cigarettes_avoided && !ci.savings_eur && (
                          <p className="text-gray-500">Check-in registrato.</p>
                        )}
                      </div>
                    )
                  ) : (
                    <p className="text-sm text-gray-500">Nessun check-in registrato.</p>
                  )}
                </div>

                {/* Rewards List section (es. 6) */}
                {!!getCI(dayData)?.ricompense_obiettivi && (
                  <div className="p-4 border rounded-xl bg-yellow-50 border-yellow-200">
                    <h3 className="font-semibold mb-3 flex items-center gap-2 text-yellow-800">
                      <Award /> Lista Ricompense Definite
                    </h3>
                    <RewardsContent ricompense={getCI(dayData).ricompense_obiettivi} />
                  </div>
                )}

                {/* Activities section */}
                <div className="p-4 border rounded-xl">
                  <h3 className="font-semibold mb-4">Attività Giornaliere</h3>
                  <div className="space-y-3">
                    <ActivityItem icon={Feather} label="Lettera di motivazione (Es. 5)"
                      completed={dayData?.activities?.esercizio_5_completato} />
                    <ActivityItem icon={Book} label="Diario del futuro (Es. 7)"
                      completed={dayData?.activities?.esercizio_7_completato} />
                    <ActivityItem icon={Wind} label="Esercizio di respirazione (Es. 9)"
                      completed={dayData?.activities?.esercizio_9_completato} />
                    <ActivityItem icon={Target} label="Abitudini potenzianti (Es. 10)"
                      completed={dayData?.activities?.abitudini_potenzianti_completate} />
                  </div>
                </div>

                {/* Note section */}
                <div className="p-4 border rounded-xl">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-semibold">Nota Personale</h3>
                    {!isEditingNote && (
                      <Button variant="ghost" size="sm" onClick={() => setIsEditingNote(true)}>
                        <Edit className="w-4 h-4 mr-2" />Modifica
                      </Button>
                    )}
                  </div>
                  {isEditingNote ? (
                    <div className="space-y-2">
                      <Textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Scrivi qui le tue riflessioni..."
                        rows={4}
                      />
                      <Button onClick={handleSaveNote} disabled={isSaving}>
                        <Save className="w-4 h-4 mr-2" />{isSaving ? "Salvataggio..." : "Salva nota"}
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-600 whitespace-pre-wrap min-h-[50px]">
                      {note || "Nessuna nota per questo giorno."}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* -------- Ricompense: supporta sia JSON string che oggetto; sia chiavi ricompensa_* che label ("1 Settimana", ...) -------- */
const RewardsContent = ({ ricompense }) => {
  const normalize = (val) => {
    let o = val;

    // Se è stringa prova a parsarla
    if (typeof o === 'string') {
      try { o = JSON.parse(o); } catch { return { __PLAIN: o }; }
    }

    // se è wrapper { rewards: {...} }
    if (o && typeof o === 'object' && o.rewards && typeof o.rewards === 'object') {
      o = o.rewards;
    }

    if (!o || typeof o !== 'object') return {};

    const out = {};
    for (const p of periodiRicompense) {
      const byId    = o[p.id];
      const byLabel = o[p.labelCanon] || o[p.label] || o[(p.labelCanon || '').toLowerCase()];
      const v = byId ?? byLabel;
      if (v != null && v !== '') out[p.id] = String(v);
    }
    return out;
  };

  const data = normalize(ricompense);

  if (data.__PLAIN) {
    return <p className="text-sm text-gray-700 whitespace-pre-wrap">{data.__PLAIN}</p>;
  }

  const hasAny = Object.keys(data).length > 0;
  if (!hasAny) return <p className="text-sm text-gray-500">Nessuna ricompensa impostata.</p>;

  return (
    <ul className="space-y-2 text-sm">
      {periodiRicompense.map(p => {
        const answer = data[p.id];
        if (!answer) return null;
        return (
          <li key={p.id}>
            <strong className="text-gray-800">{p.label}:</strong>
            <p className="text-gray-600 pl-2 whitespace-pre-wrap">{answer}</p>
          </li>
        );
      })}
    </ul>
  );
};
